const RECORDER_MIME_CANDIDATES = ['video/webm;codecs=vp8,opus', 'video/webm;codecs=vp9,opus', 'video/webm', 'video/mp4']

/** First MediaRecorder mime the browser accepts. Empty string lets the UA pick a default. */
export function getSupportedRecorderMimeType(): string {
    if (typeof MediaRecorder === 'undefined' || typeof MediaRecorder.isTypeSupported !== 'function') {
        return RECORDER_MIME_CANDIDATES[0]
    }
    return RECORDER_MIME_CANDIDATES.find((type) => MediaRecorder.isTypeSupported(type)) ?? ''
}

export function cloneTrackForRecording(track: MediaStreamTrack): MediaStreamTrack | null {
    if (track.readyState === 'ended') {
        return null
    }
    try {
        return track.clone()
    } catch {
        return null
    }
}

export type RecordingChannel = 0 | 1
/** Candidate mic → left, AI interviewer → right (INT-983). */
export const CANDIDATE_RECORDING_CHANNEL: RecordingChannel = 0
export const AGENT_RECORDING_CHANNEL: RecordingChannel = 1

type AudioContextWithTrackSource = AudioContext & {
    createMediaStreamTrackSource?: (track: MediaStreamTrack) => MediaStreamAudioSourceNode
}

/**
 * Connect a mono track into one ChannelMerger input: the candidate mic records on the left channel and the AI
 * interviewer on the right (the same layout as Plivo phone recordings), so talent-ai can re-transcribe the candidate
 * from the recording on its own (INT-983). The HLS conversion mixes both channels back into both ears for playback.
 * When `gate` is provided the signal routes through it instead — the caller connects the gate to the merger once and
 * can mute the path via gain.
 * Returns the cloned track when connected (caller should stop it on teardown).
 */
export function connectTrackToRecordingMixer(
    audioContext: AudioContext,
    merger: ChannelMergerNode,
    track: MediaStreamTrack,
    gate?: GainNode | null,
    channel: RecordingChannel = AGENT_RECORDING_CHANNEL
): MediaStreamTrack | null {
    const clonedTrack = cloneTrackForRecording(track)
    if (!clonedTrack) {
        return null
    }

    const ctx = audioContext as AudioContextWithTrackSource
    let source: AudioNode
    if (typeof ctx.createMediaStreamTrackSource === 'function') {
        source = ctx.createMediaStreamTrackSource(clonedTrack)
    } else {
        source = audioContext.createMediaStreamSource(new MediaStream([clonedTrack]))
    }

    // High-pass filter to remove low-frequency rumble/hum before mixing.
    const highPass = audioContext.createBiquadFilter()
    highPass.type = 'highpass'
    highPass.frequency.value = 80
    source.connect(highPass)

    if (gate) {
        highPass.connect(gate)
    } else {
        highPass.connect(merger, 0, channel)
    }

    return clonedTrack
}

/**
 * Connect silent audio into unused merger inputs so the destination stays
 * 2-channel from MediaRecorder.start() (bot audio often arrives later).
 */
export function connectSilentKeepAliveToMerger(audioContext: AudioContext, merger: ChannelMergerNode, occupiedChannels: Set<0 | 1>): OscillatorNode {
    const silence = audioContext.createOscillator()
    silence.frequency.value = 0
    const gain = audioContext.createGain()
    gain.gain.value = 0
    silence.connect(gain)

    for (const channel of [0, 1] as const) {
        if (!occupiedChannels.has(channel)) {
            gain.connect(merger, 0, channel)
        }
    }

    silence.start()
    return silence
}
